/**
 * @file index.jsx
 * @brief Displays the authenticated user's account settings and profile editing form.
 */
import {
    useEffect, useState, useMemo, useRef
} from "react";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { createApiClient } from "../../api/axios";
import { getUserDisplayName } from "../../utils/userDisplayName";
import styles from "./account.module.css";

/**
 * Render the account page used to edit the current user's bio and profile picture.
 * @returns {JSX.Element} The account settings screen.
 */
export default function Account() {
    const auth = useAuth();
    const { t } = useTranslation();
    const accessToken = auth.user?.access_token;
    const api = useMemo(
        () => createApiClient({
            isAuthenticated: auth.isAuthenticated,
            user: accessToken ? { access_token: accessToken } : null,
        }),
        [accessToken, auth.isAuthenticated]
    );

    const [bio, setBio] = useState("");
    const [profilePicture, setProfilePicture] = useState(null);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const avatarUrlRef = useRef(null);
    const fileInputRef = useRef(null);

    /**
        * Replace the currently displayed avatar preview URL.
        * @param {string|null} url Blob URL used for the avatar preview.
        * @returns {void}
     */
    const setAvatar = (url) => {
        avatarUrlRef.current = url;
        setAvatarUrl(url);
    };

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            try {
                const res = await api.get("userprofiles/own/");
                setBio(res.data.bio || "");

                if (res.data.profile_picture_url) {
                    const avatarRes = await fetch(res.data.profile_picture_url, {
                        headers: { Authorization: `Bearer ${accessToken}` },
                    });
                    if (avatarRes.ok) {
                        const blob = await avatarRes.blob();
                        setAvatar(URL.createObjectURL(blob));
                    }
                }
            } catch (err) {
                setError(t("account.loadError"));
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) fetchProfile();

        return () => {
            if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
        };
    }, [accessToken, api, t]);

    // Auto-dismiss success banner
    useEffect(() => {
        if (!success) return;
        const t = setTimeout(() => setSuccess(false), 3000);
        return () => clearTimeout(t);
    }, [success]);

    /**
        * Load a newly selected profile picture into the local preview state.
        * @param {Event} e File input change event.
        * @returns {void}
     */
    const handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
        setProfilePicture(file);
        setAvatar(URL.createObjectURL(file));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            const formData = new FormData();
            formData.append("bio", bio);
            if (profilePicture) formData.append("profile_picture", profilePicture);
            await api.patch("userprofiles/own/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setSuccess(true);
        } catch (err) {
            setError(t("account.updateError"));
        } finally {
            setSaving(false);
        }
    };

    const userIdentity = getUserDisplayName({
        username: auth.user?.profile?.preferred_username || auth.user?.profile?.username,
        first_name: auth.user?.profile?.given_name,
        last_name: auth.user?.profile?.family_name,
    });

    if (loading) return (
        <div className={styles.stateCenter}>
            <div className={styles.spinner} />
            <p>{t("account.loading")}</p>
        </div>
    );

    return (
        <div className={styles.container}>
            <div className={styles.card}>

                <h2 className={styles.title}>{t("account.title")}</h2>

                <form onSubmit={handleSubmit}>

                    {/* ── Avatar ── */}
                    <div className={styles.avatarSection}>
                        <div className={styles.avatarWrapper}>
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="Profile" className={styles.avatar} />
                            ) : (
                                <div className={styles.avatarPlaceholder}>
                                    {userIdentity.avatarInitial}
                                </div>
                            )}
                            <button
                                type="button"
                                className={styles.avatarEditBtn}
                                onClick={() => fileInputRef.current?.click()}
                                aria-label={t("account.changeProfilePicture")}
                            >
                                ✏️
                            </button>
                        </div>

                        <div className={styles.avatarMeta}>
                            <p className={styles.avatarName}>{userIdentity.primaryName}</p>
                            {userIdentity.secondaryName && (
                                <p className={styles.avatarUsername}>{userIdentity.secondaryName}</p>
                            )}
                            <button
                                type="button"
                                className={styles.changePhotoBtn}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {t("account.changePhoto")}
                            </button>
                            {profilePicture && (
                                <p className={styles.fileChosen}>📎 {profilePicture.name}</p>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className={styles.fileInput}
                            onChange={handleProfilePictureChange}
                        />
                    </div>

                    {/* ── Bio ── */}
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="bio">{t("account.bio")}</label>
                        <textarea
                            id="bio"
                            className={styles.textarea}
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            placeholder={t("account.bioPlaceholder")}
                        />
                        <span className={styles.charCount}>{t("account.characters", { count: bio.length })}</span>
                    </div>

                    {/* ── Feedback ── */}
                    {error && (
                        <div className={styles.errorBanner}>
                            ⚠️ {error}
                        </div>
                    )}
                    {success && (
                        <div className={styles.successBanner}>
                            ✓ {t("account.updatedSuccess")}
                        </div>
                    )}

                    {/* ── Actions ── */}
                    <div className={styles.actions}>
                        <button type="submit" className={styles.saveButton} disabled={saving}>
                            {saving ? (
                                <><span className={styles.btnSpinner} /> {t("common.saving")}</>
                            ) : (
                                t("account.saveProfile")
                            )}
                        </button>

                        <Link to="/account/listings" className={styles.linkButton}>
                            {t("account.myListings")} →
                        </Link>
                    </div>

                </form>
            </div>
        </div>
    );
}
